/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import JSG from '@cedalo/jsg-ui';
import { getPluginPanelSize, getPluginSettingsComponent, hasPluginSettings } from '@cedalo/webui-extensions';
import { Box } from '@mui/material';
import { useTour } from '@reactour/tour';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import * as Actions from '../../actions/actions';
import { graphManager } from '../../GraphManager';
import { useLocale } from '../../languages/LocaleProvider';
import { withUser } from '../../UserProvider';
import { AppActions } from '../app/AppActions';
import { AppControl } from '../app/AppControl';
import AppName from '../app/AppName';
import { withAppData } from '../app/AppProvider';
import { AppSettings } from '../app/AppSettings';
import { ShareSettings } from '../app/ShareSettings';
import { ConnectionDetailView, ConnectionTable, NewConnection } from '../sheeteditor/connections';
import DeleteCellContentDialog from '../sheeteditor/dialogs/DeleteCellContentDialog';
import DeleteCellsDialog from '../sheeteditor/dialogs/DeleteCellsDialog';
import InsertCellContentDialog from '../sheeteditor/dialogs/InsertCellContentDialog';
import InsertCellsDialog from '../sheeteditor/dialogs/InsertCellsDialog';
import { IntegrationSelectPanel } from '../sheeteditor/integrations';
import { InboxSettings } from '../sheeteditor/settings/InboxSettings';
import { InsertFunctionSettings } from '../sheeteditor/settings/InsertFunctionSettings';
import LayoutSectionSettings from '../sheeteditor/settings/LayoutSectionSettings';
import { NameSettings } from '../sheeteditor/settings/NameSettings';
import { NumberFormatSettings } from '../sheeteditor/settings/NumberFormatSettings';
import { ShapeListSettings } from '../sheeteditor/settings/ShapeListSettings';
import { ShapeSettings } from '../sheeteditor/settings/ShapeSettings';
import { SheetSettings } from '../sheeteditor/settings/SheetSettings';
import SheetComponent from '../sheeteditor/SheetComponent';
import SheetEditBar from '../sheeteditor/SheetEditBar';
import SheetToolBar from '../sheeteditor/SheetToolBar';
import { StreamSheetAppBar } from '../StreamSheetAppBar';
import StreamSheetNavigation from '../StreamSheetNavigation';
import { useJSGNotification } from '../utils/JSGHooks';
import { clearSidePanelHash, parseSidePanelHash, setSidePanelHash } from '../utils/SidePanel';
import StreamSheetBreadCrumbs from '../utils/StreamSheetBreadCrumbs';
import StreamSheetPaper from '../utils/StreamSheetPaper';
import TourButton from '../utils/TourButton';
import { getHash, setHash } from '../utils/URLHash';
import { LoadingPage } from './LoadingPage';

const getActiveSheetId = () => {
	const view = graphManager.getActiveSheetView();
	if (!view) {
		return JSG.streamSheets.length ? JSG.streamSheets[0].id : undefined;
	}
	return view.getItem().model.id;
};

function AppPageComponent(props) {
	const { appId, appName, viewSettings, appEditable, appState } = props;
	const { locale } = useLocale();
	const navigate = useNavigate();
	const location = useLocation();
	const [connectionBack, setConnectionBack] = useState();
	const { setIsOpen, setSteps, setCurrentStep } = useTour();

	const setConnectionHistory = (id) => {
		switch (id) {
			case 'Connections':
				setConnectionBack('#panel=/Connections/');
				break;
			case 'InboxSettings':
				setConnectionBack('#panel=/InboxSettings/');
				break;
			default:
				setConnectionBack();
				break;
		}
	};

	const [sidePanel, setSidePanel] = useState(() => {
		const id = parseSidePanelHash(location.hash);
		setConnectionBack(id);
		setConnectionHistory(id.id);
		return id;
	});

	useEffect(() => {
		setSidePanel(parseSidePanelHash(location.hash));
	}, [location.hash]);

	const [showDeleteCellContentDialog, setShowDeleteCellContentDialog] = useState(false);
	const [showInsertCellContentDialog, setShowInsertCellContentDialog] = useState(false);
	const [showViewMode, setShowViewMode] = useState(
		window.location.href.indexOf('preview') !== -1 || getHash().preview || false
	);
	const [activeSheetId, setActiveSheetId] = useState(getActiveSheetId());
	const [splitView, setSplitView] = useState(false);
	const [mediaSize, setMediaSize] = useState('large');
	const [mediaWidth, setMediaWidth] = useState(0);
	const [sidePanelSize, setSidePanelSize] = useState(300);
	const [userSelections, setUserSelections] = useState();

	const onClose = (notification) => {
		if (notification.object) {
			const info = notification.object;
			if (info.button.getName().getValue() === 'closeInbox') {
				props.saveProcessSettings({
					machineId: graphManager.graph.machineContainer.machineId,
					streamsheetId: info.container.model.id,
					settings: {
						showInbox: false
					}
				});
			}
			if (info.button.getName().getValue() === 'closeOutbox') {
				props.updateMachineSettings(info.container.machineContainer.machineId, { showOutbox: false });
				props.updateAppSettings({ showOutbox: false });
			}
		}
	};

	const onSplitViewChange = () => {
		const graph = graphManager.getGraph();
		const viewer = graphManager.getGraphViewer();

		const graphController = viewer.getGraphController();
		const sheetItem = graph.streamSheetsContainer.leftSheetContainer.streamSheet;
		const controller = graphController.getControllerByModelId(sheetItem.getId());
		if (!controller) {
			return;
		}
		const view = controller.getView();

		view.notifySelectionChange(viewer);
		graph.streamSheetsContainer.layout();
		graphManager.redraw();
		graphManager.getCanvas().focus();

		setSplitView(graph.machineContainer.splitView);

		let lru = localStorage.getItem(`LRU1`);
		if (lru) {
			lru = lru.split(';');
			if (lru.length > 1) {
				lru[1] = graph.machineContainer.splitView ? '1' : '0';
				lru = lru.join(';');
				localStorage.setItem(`LRU1`, lru);
			}
		}
	};

	const handleResize = () => {
		const el = document.getElementById('sheet-toolbar');
		if (!el) {
			setMediaSize('large');
			return;
		}
		const { width } = el.getBoundingClientRect();
		setMediaWidth(width);
		if (width > 1200) {
			setMediaSize('large');
		} else if (width > 950) {
			setMediaSize('medium');
		} else {
			setMediaSize('small');
		}
	};

	const onShapeSelection = () => {
		const { graph } = graphManager;
		const viewer = graphManager.getGraphViewer();
		const selection = viewer.getSelectionProvider().getFirstSelection();
		if (!selection) {
			return;
		}
		let item = selection.getModel().getParent();
		while (item && !(item instanceof JSG.StreamSheetContainer)) {
			item = item.getParent();
		}

		if (item) {
			graphManager.graph.activeSheetContainer = item;
		}

		// remove selections sheets
		if (graph.sheetSelection.hasSelection() && item) {
			const sheet = item.streamSheet;
			graphManager.removeSelection(sheet.getSelectionId());
		}

		if (item) {
			const graphController = viewer.getGraphController();
			const controller = graphController.getControllerByModelId(item.getStreamSheet().getId());
			if (controller) {
				viewer.getGraphView().activeView = controller.getView();
			}
		}
	};

	const onShapeCreated = (notification) => {
		const { event } = notification;

		if (event.id !== 'event.itemadd') {
			return;
		}

		const item = event.value;
		if (!(item instanceof JSG.SheetPlotNode)) {
			return;
		}

		const attr = item.getAttributeAtPath('showwizard');
		if (attr && attr.getValue() === true) {
			const viewer = graphManager.getGraphViewer();
			const focus = viewer.getGraphView().getFocus();
			const treeSelected = focus && focus.getView() instanceof JSG.TreeItemsView;
			if (treeSelected) {
				item.createSeriesFromInbox(
					{ action: 'replaceSeries', actionSeriesIndex: -1 },
					focus.getModel().model,
					viewer,
					graphManager.chartType
				);
			} else {
				const sheetView = graphManager.getActiveSheetView();
				item.createSeriesFromSelection(
					viewer,
					sheetView.getItem(),
					graphManager.chartSelection,
					graphManager.chartType
				);
			}
			graphManager.getGraphEditor().invalidate();
			attr.setExpressionOrValue(false);
		}
	};

	const onUserSelection = () => {
		const { graph } = graphManager;
		const users = [];
		const selUsers = graph.graph._userSelections.elements();
		selUsers.forEach((selUser) => {
			if (selUser.selectionId) {
				const data = selUser.selectionId.split(';');
				if (data.length === 3) {
					const name = data[2];
					if (name.length) {
						if (!users.find((user) => name === user)) {
							users.push(name);
						}
					}
				}
			}
		});
		setUserSelections(users);
	};

	useJSGNotification('userSelections', onUserSelection);
	useJSGNotification(JSG.SelectionProvider.SELECTION_CHANGED_NOTIFICATION, onShapeSelection);
	useJSGNotification(JSG.GraphItemController.ITEM_CHANGED_NOTIFICATION, onShapeCreated);
	useJSGNotification(JSG.ButtonNode.BUTTON_CLICKED_NOTIFICATION, onClose);

	const restoreView = () => {
		let lru = localStorage.getItem(`LRU1`);
		if (lru) {
			const graph = graphManager.graph;
			if (!graph.showViewMode) {
				lru = lru.split(';');
				if (lru.length > 1 && lru[1] === '1') {
					graph.machineContainer.splitView = true;
					if (lru.length > 3 && lru[3] !== '0' && JSG.Numbers.canBeNumber(lru[3])) {
						graph.streamSheetsContainer.rightSheetContainer.setActiveSheetIndex(
							Number(lru[3]),
							graphManager.getGraphViewer()
						);
					}
					onSplitViewChange();
				}
				if (lru.length > 2 && lru[2] !== '0' && JSG.Numbers.canBeNumber(lru[2])) {
					graph.streamSheetsContainer.leftSheetContainer.setActiveSheetIndex(
						Number(lru[2]),
						graphManager.getGraphViewer()
					);
				}
			}
		}
	};

	// add an effect on mount tha twill watch for size changes in the
	// element inside the ref
	useEffect(() => {
		const resizeObserver = new ResizeObserver(handleResize);
		const el = document.getElementById('sheet-toolbar');
		if (el) {
			// resize observer is a tool you can use to watch for size changes efficiently
			resizeObserver.observe(el);
		}

		restoreView();

		// set active sheet after app load (DL-6070)
		setActiveSheetId(getActiveSheetId());

		return () => resizeObserver.disconnect();
	}, []);

	useEffect(() => {
		setHash({ preview: showViewMode ? true : undefined });
	}, [showViewMode]);

	useEffect(() => {
		graphManager.setUILanguage(locale);
	}, [locale]);

	// Update canvas if viewMode changes
	useLayoutEffect(() => {
		if (showViewMode) {
			graphManager.graph.machineContainer.splitView = false;
		} else {
			restoreView();
		}
		graphManager.updateCanvas(viewSettings, showViewMode);
		graphManager.redraw();
	}, [viewSettings, showViewMode]);

	useEffect(() => {
		document.title = appName; // intl.formatMessage({ id: 'TabTitle.App' }, { name: appName || 'App' });
	}, [appName]);

	useLayoutEffect(() => {
		handleResize();
		window.dispatchEvent(new Event('resize'));
		graphManager.updateDimensions();
		graphManager.redraw();
	}, [sidePanel.id]);

	useEffect(() => {
		if (!sidePanel.id) {
			setSidePanelSize(showViewMode ? '0' : '32');
		} else {
			const pluginPanelSize = getPluginPanelSize(sidePanel.id);
			if (pluginPanelSize) {
				setSidePanelSize(pluginPanelSize + 42);
			} else if (sidePanel.id === 'Connections') {
				setSidePanelSize('642');
			} else if (showViewMode) {
				setSidePanelSize(326);
			} else {
				setSidePanelSize(342);
			}
		}
	}, [sidePanel.id]);

	const onShowDialog = (id, data) => {
		setConnectionHistory(id);
		switch (id) {
			case 'SheetSettings':
			case 'Connections':
			case 'InboxSettings':
			case 'NumberFormatSettings':
			case 'InsertFunctionSettings':
			case 'Integrations':
			case 'NameSettings':
			case 'AppSettings':
			case 'ShapeListSettings':
			case 'ShareSettings':
			case 'LayoutSectionSettings':
			case 'ShapeSettings':
				setSidePanel({ id, data });
				setSidePanelHash(id, data);
				handleResize();
				// setTimeout(() => {
				// 	window.dispatchEvent(new Event('resize'));
				// 	graphManager.redraw();
				// }, 0);
				break;
			case 'DeleteCellContent':
				setShowDeleteCellContentDialog(true);
				break;
			case 'InsertCellContent':
				setShowInsertCellContentDialog(true);
				break;
			default:
				if (hasPluginSettings(id)) {
					setSidePanel({ id, data });
					setSidePanelHash(id, data);
					handleResize();
				}
				break;
		}
	};

	useJSGNotification('ShowInboxSettings', () => onShowDialog('InboxSettings'));
	useJSGNotification(JSG.SHOW_SHAPE_SETTINGS, () => onShowDialog('ShapeSettings'));
	useJSGNotification('ShowLayoutSectionSettings', () => onShowDialog('LayoutSectionSettings'));

	const getSidePanel = () => {
		switch (sidePanel.id) {
			case 'Connections': {
				if (sidePanel.data) {
					return sidePanel.data === 'new' ? (
						<NewConnection
							onClose={() => clearSidePanelHash()}
							onNewConnection={(connectionId) => onShowDialog('Connections', connectionId)}
						/>
					) : (
						<ConnectionDetailView
							activeSheet={activeSheetId}
							history={connectionBack}
							connectionId={sidePanel.data}
							onClose={() => clearSidePanelHash()}
						/>
					);
				}
				return (
					<ConnectionTable
						appId={appId}
						onClose={() => clearSidePanelHash()}
						onSelectConnection={(connectionId) => onShowDialog('Connections', connectionId)}
						onAddConnection={() => onShowDialog('Connections', 'new')}
					/>
				);
			}
			case 'Integrations': {
				return (
					<IntegrationSelectPanel
						appId={appId}
						onClose={() => clearSidePanelHash()}
						onSelectConnection={(integrationId) => onShowDialog('Connections', integrationId)}
						onAddConnection={() => onShowDialog('Integrations', 'new')}
					/>
				);
			}
			case 'InsertFunctionSettings':
				return (
					<InsertFunctionSettings
						// sheetId={activeSheetId}
						open
						close={() => clearSidePanelHash()}
					/>
				);
			case 'ShapeListSettings':
				return (
					<ShapeListSettings
						onSheetActivate={(id) => setActiveSheetId(id)}
						open
						close={() => clearSidePanelHash(false)}
					/>
				);
			case 'ShareSettings':
				return (
					<ShareSettings
						appId={appId}
						appEditable={appEditable}
						showViewMode={(show) => setShowViewMode(show)}
						open
						close={() => clearSidePanelHash(false)}
					/>
				);
			case 'LayoutSectionSettings':
				return <LayoutSectionSettings open close={() => clearSidePanelHash(false)} />;
			case 'ShapeSettings':
				return (
					<ShapeSettings showDialog={(id) => onShowDialog(id)} open close={() => clearSidePanelHash(false)} />
				);
			case 'SheetSettings':
				return <SheetSettings sheetId={activeSheetId} open close={() => clearSidePanelHash(false)} />;
			case 'InboxSettings':
				return <InboxSettings sheetId={activeSheetId} open close={() => clearSidePanelHash(false)} />;
			case 'NameSettings':
				return <NameSettings open close={() => clearSidePanelHash(false)} />;
			case 'NumberFormatSettings':
				return (
					<NumberFormatSettings
						open
						close={() => clearSidePanelHash(false)}
						// selection={cellSelection}
						locale={locale}
					/>
				);
			case 'AppSettings':
				return <AppSettings open close={() => clearSidePanelHash(false)} />;
			default: {
				const PluginSettings = getPluginSettingsComponent(sidePanel.id);
				return PluginSettings ? <PluginSettings open onClose={() => clearSidePanelHash(false)} /> : null;
			}
		}
	};

	const getAppState = () => {
		switch (appState) {
			case 'stopped':
				return props.intl.formatMessage({ id: 'AppBar.Stopped' }, {});
			case 'paused':
				return props.intl.formatMessage({ id: 'AppBar.Paused' }, {});
			case 'running':
				return props.intl.formatMessage({ id: 'AppBar.Running' }, {});
			default:
				break;
		}
		return '';
	};

	const drawerWidth = showViewMode ? 0 : 60;

	return (
		<div style={{ width: 'inherit', height: 'inherit' }}>
			{showViewMode ? null : (
				<div>
					<StreamSheetAppBar
						nameEditor={<AppName name={appName || ''} appId={appId} />}
						appControl={<AppControl appEditable={appEditable} />}
						appState={getAppState()}
						key="appPageBar"
						users={userSelections}
						helpContext="app"
					/>
					<StreamSheetNavigation openPage={navigate} appName={appName} appId={appId} key="appPageNav" />
				</div>
			)}
			<Box
				onKeyDown={(event) => {
					if (event.shiftKey && event.ctrlKey) {
						// eslint-disable-next-line default-case
						switch (event.key) {
							case 'I':
								onShowDialog('InboxSettings');
								break;
							case 'S': {
								const cont = graphManager.graph.machineContainer;
								cont.splitView = !cont.splitView;
								onSplitViewChange();
								break;
							}
							case 'F':
								onShowDialog('Files');
								break;
							case 'A':
								onShowDialog('AppSettings');
								break;
							case 'L':
								onShowDialog('ShapeListSettings');
								break;
							case 'Q':
								onShowDialog('SheetSettings');
								break;
							default:
								return;
						}
						event.preventDefault();
					}
				}}
				sx={{
					position: 'relative',
					height: `calc(100% - ${showViewMode ? 0 : 50}px)`,
					width: `calc(100% - ${drawerWidth}px)`,
					marginLeft: `${drawerWidth}px`,
					overflow: 'hidden',
					bgcolor: (theme) => theme.wall.backgroundColor
				}}
				key="appPageBox"
				data-tour="stream-app-page"
			>
				{showViewMode ? null : (
					<div
						style={{
							display: 'inline-flex',
							width: 'calc(100% - 15px)',
							justifyContent: 'space-between',
							marginBottom: '3px'
						}}
					>
						<div style={{ display: 'inline-flex' }}>
							<StreamSheetBreadCrumbs
								path={`Home/Apps/${appName}`}
								workSpace={props.workSpace}
								openPage={navigate}
							/>
							{props.appInfo.tour && props.appInfo.tour.name ? (
								<TourButton
									appTour
									runTour={() => {
										setSteps(props.appInfo.tour.steps);
										setCurrentStep(0, props.appInfo.tour.steps);
										setIsOpen(true);
									}}
								/>
							) : null}
						</div>
						<AppActions
							appId={appId}
							showViewMode={(show) => setShowViewMode(show)}
							showDialog={(id) => onShowDialog(id)}
							appEditable={appEditable}
						/>
					</div>
				)}
				{/*<Box*/}
				{/*	sx={{*/}
				{/*		// eslint-disable-next-line no-nested-ternary*/}
				{/*		height: `calc(100% - ${showViewMode ? (sidePanel.id ? 32 : 0) : 55}px)`,*/}
				{/*		marginTop: showViewMode && sidePanel.id ? '15px' : '0px',*/}
				{/*		position: 'relative',*/}
				{/*		display: 'flex'*/}
				{/*	}}*/}
				{/*>*/}
				<StreamSheetPaper data-tour="page-app" preview={showViewMode} topSpace={0} bottomSpace={0}>
					<Box
						sx={{
							height: 'calc(100%)',
							width: `calc(100% - ${sidePanelSize}px)`
							// width: sidePanel.id ? 'calc(100% - 342px)' : 'calc(100% - 32px)'
						}}
						data-tour="page-app"
					>
						{showViewMode
							? null
							: [
									<SheetToolBar
										key="sheetToolBar"
										splitView={splitView}
										onSplitViewChange={onSplitViewChange}
										showDialog={(id) => onShowDialog(id)}
										mediaSize={mediaSize}
										appEditable={appEditable}
									/>,
									<SheetEditBar key="sheetEditBar" showDialog={(id) => onShowDialog(id)} />
							  ]}
						<div
							style={{
								height: `calc(100% - ${showViewMode ? 0 : 83}px)`,
								position: 'relative'
							}}
						>
							<SheetComponent
								appId={appId}
								splitView={splitView}
								mediaWidth={mediaWidth}
								showViewMode={showViewMode}
								showDialog={(id) => onShowDialog(id)}
								onSheetActivate={(id) => setActiveSheetId(id)}
								canEditMachine={appEditable}
							/>
						</div>
					</Box>
					{getSidePanel()}
				</StreamSheetPaper>
				{/*</Box>*/}

				<InsertCellsDialog />
				<DeleteCellsDialog />
				<DeleteCellContentDialog
					open={showDeleteCellContentDialog}
					close={() => setShowDeleteCellContentDialog(false)}
				/>
				<InsertCellContentDialog
					open={showInsertCellContentDialog}
					close={() => setShowInsertCellContentDialog(false)}
				/>
			</Box>
		</div>
	);
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({ ...Actions }, dispatch);
}

const Spinner = withUser(({ spinner }) => ({ spinner }))(({ spinner }) => <LoadingPage>{spinner}</LoadingPage>);

export const AppPage = withAppData(({ machine, dispatchAppData }) => ({
	appId: machine.id,
	appName: machine.name,
	viewSettings: machine.settings.view,
	appEditable: machine.canEdit,
	appState: machine.state,
	appLocale: machine.settings.locale,
	appInfo: {
		tour: machine.settings.tour,
		image: machine.settings.image,
		categories: machine.settings.categories
	},
	workSpace: machine.scope.id,
	updateAppSettings: (settings) => dispatchAppData({ type: 'update-machine-settings', data: settings })
}))(injectIntl(connect(null, mapDispatchToProps)(AppPageComponent)), Spinner);
